import React from "react";
import {
  SwanPublicClient,
  StreamUpdate,
  ExchangeRate
} from "swan-public-api-client";

interface Props {
  swanApiClient: SwanPublicClient;
}

export type FxRate = number | undefined;

export interface FxRates {
  getAndOrSubscribeFxRate: (fromCurrency: string, toCurrency: string) => FxRate;
}

interface State {
  fxRates: { [fxRateKey: string]: FxRate };
  inFlight: { [fxRateKey: string]: boolean };
}

const FxRatesContext = React.createContext({} as FxRates);

class FxRatesProviderCmp extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      fxRates: {},
      inFlight: {}
    };
  }
  public render() {
    return (
      <FxRatesContext.Provider
        value={{
          getAndOrSubscribeFxRate: this.getAndOrSubscribeFxRate
        }}
      >
        {this.props.children}
      </FxRatesContext.Provider>
    );
  }
  public shouldComponentUpdate(nextProps: Props, nextState: State) {
    if (nextProps === this.props && nextState.fxRates === this.state.fxRates) {
      // the state of in-flight values is the only thing that changed
      return false;
    }
    return true;
  }
  private getAndOrSubscribeFxRate = (
    fromCurrency: string,
    toCurrency: string
  ): FxRate => {
    const { fxRates, inFlight } = this.state;
    const fxRateKey = this.createKey(fromCurrency, toCurrency);
    const fxRate = fxRates[fxRateKey];
    // we avoid subscribing and making REST requests multiple times for the same FX rate
    const isInFlight = inFlight[fxRateKey];
    if (fxRate === undefined && isInFlight !== true) {
      this.storeInFlight(fxRateKey);
      this.fetchAndSubscribeToFxRate(fromCurrency, toCurrency, fxRateKey);
    }
    return fxRate;
  };
  private fetchAndSubscribeToFxRate = async (
    fromCurrency: string,
    toCurrency: string,
    fxRateKey: string
  ) => {
    const fxRateFetchResult = await this.tryFetch(
      fromCurrency,
      toCurrency,
      fxRateKey
    );
    if (fxRateFetchResult === undefined) {
      return;
    }
    this.storeFxRateChange(fxRateKey, fxRateFetchResult.fxRate);
    const stream = this.props.swanApiClient.streamExchangeRates(
      fxRateFetchResult.subscriptionKey,
      this.consumeUpdate,
      undefined,
      0.1
    );
    // these streams are not `stop()`ped. but we want to remain subscribed to FX rate pairs for the lifetime of the app anyway
    stream.start();
  };
  private storeInFlight(fxRateKey: string) {
    const { inFlight } = this.state;
    inFlight[fxRateKey] = true;
    const newInFlight = { ...inFlight };
    this.setState({ inFlight: newInFlight });
  }
  private clearInFlight(fxRateKey: string) {
    const { inFlight } = this.state;
    inFlight[fxRateKey] = false;
    const newInFlight = { ...inFlight };
    this.setState({ inFlight: newInFlight });
  }
  private async tryFetch(
    fromCurrency: string,
    toCurrency: string,
    fxRateKey: string
  ): Promise<{ fxRate: number; subscriptionKey: string } | undefined> {
    let subscriptionKey: string;
    let fxRate: number;
    try {
      const { Content } = await this.props.swanApiClient.getExchangeRate(
        fromCurrency,
        toCurrency
      );
      subscriptionKey = Content.Key;
      fxRate = Content.Rate;
    } catch {
      // there is no fx rate or there was a comms issue
      // when the rate is not present in swan then many fetch requests may be sent. this should never be the case
      // when the rate is temporarily unavailable then allow components to fetch again
      this.clearInFlight(fxRateKey);
      return undefined;
    }
    return { fxRate, subscriptionKey };
  }
  private storeFxRateChange = (fxRateKey: string, fxRate: number) => {
    const { fxRates } = this.state;
    fxRates[fxRateKey] = fxRate;

    const newFxRates = {
      ...fxRates
    };
    this.setState({
      fxRates: newFxRates
    });
  };
  private consumeUpdate = (
    updatedExchangeRates: Array<StreamUpdate<ExchangeRate>>
  ) => {
    const { fxRates } = this.state;
    updatedExchangeRates.forEach((x: StreamUpdate<ExchangeRate>) => {
      fxRates[this.createKey(
        x.update.FromCurrency.toString(),
        x.update.ToCurrency.toString()
      )] = x.update.Rate
    });
    const newFxRates = { ...fxRates };
    this.setState({ fxRates: newFxRates });
  };
  private createKey = (fromCurrency: string, toCurrency: string): string => {
    return `${fromCurrency}#${toCurrency}`;
  };
}

export const FxRatesConsumer = FxRatesContext.Consumer;

export const FxRatesProvider = FxRatesProviderCmp;
